import {
  useId,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type RefObject,
} from 'react';
import { T, useGT } from 'gt-react';
import { Checkbox } from '@/components/ui/checkbox';
import { PaperPlaneTilt as SendIcon } from '@phosphor-icons/react';
import { Button } from 'src/components/ui/button';
import { cn } from '@/lib/utils';
import {
  composerKeyAction,
  filterMentionCandidates,
  findActiveMentionQuery,
  dropBrokenMentions,
  insertMention,
  placeMention,
  retargetMentions,
  serializeMentions,
  stepHighlight,
  type ActiveMentionQuery,
  type ComposerKeyAction,
  type MentionCandidate,
  type TrackedMention,
} from './mention-input';

export interface NoteComposerProps {
  /**
   * Posts the serialized body. Resolve `true` to clear the composer; `false`
   * keeps the draft so the author can retry.
   */
  onSubmit: (body: string, internal: boolean) => Promise<boolean>;
  isSubmitting?: boolean;
  /** Offer an "Internal note" toggle (hidden from non-triagers). */
  allowInternalNotes?: boolean;
  /** People who can be @mentioned. Omit to turn mentions off. */
  mentionableUsers?: readonly MentionCandidate[];
  /** Keep the field visible, but do not accept another note. */
  disabled?: boolean;
  className?: string;
  /** Show the native bottom-right handle so the field can grow taller. */
  resizable?: boolean;
  /** Replaces the default "Add a note…" prompt (e.g. "Reply…"). */
  placeholder?: string;
  /**
   * Where the @mention list opens: `overlay` (default) floats above the
   * field; `inline` takes its own line under it, in the flow. Use `inline`
   * inside a box that clips overflow (a scrolling popover), where a list
   * floating above a field near its top would be cut off.
   */
  mentionListPlacement?: MentionListPlacement;
}

export type MentionListPlacement = 'overlay' | 'inline';

const MENTION_LIST_CLASS: Record<MentionListPlacement, string> = {
  overlay:
    'absolute bottom-full left-0 z-20 mb-1 max-h-56 w-64 overflow-y-auto rounded-md border bg-popover p-1 text-xs shadow-md',
  inline:
    'order-last max-h-56 w-full basis-full overflow-y-auto rounded-md border bg-popover p-1 text-xs',
};

const NO_CANDIDATES: readonly MentionCandidate[] = [];
const TEXTAREA_BASE_CLASS =
  'min-h-8 min-w-0 flex-1 rounded-md border border-input bg-background px-2 py-1.5 text-xs placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50';

function composerLocked(disabled: boolean, isSubmitting: boolean): boolean {
  return disabled || isSubmitting;
}

function composerFieldClass(resizable: boolean): string {
  if (resizable) {
    return cn(
      TEXTAREA_BASE_CLASS,
      'h-8 max-h-none resize-y overflow-auto [field-sizing:fixed]',
    );
  }
  return cn(TEXTAREA_BASE_CLASS, 'max-h-32 resize-none [field-sizing:content]');
}

function composerPlaceholder(
  gt: ReturnType<typeof useGT>,
  mentionsEnabled: boolean,
): string {
  if (mentionsEnabled) {
    return gt('Add a note… Type @ to mention someone', {
      context: 'placeholder for a new note on a record that supports @mentions',
    });
  }
  return gt('Add a note…', {
    context: 'placeholder for a new note on a record',
  });
}

function nextDraftMentions(
  current: TrackedMention[],
  previousText: string,
  nextText: string,
  range: { start: number; end: number } | null,
): TrackedMention[] {
  if (range === null) {
    return dropBrokenMentions(nextText, current);
  }
  return retargetMentions(
    current,
    previousText,
    nextText,
    range.start,
    range.end,
  );
}

function runComposerKey(
  event: KeyboardEvent<HTMLTextAreaElement>,
  pickerOpen: boolean,
  handlers: Record<ComposerKeyAction, () => void>,
): void {
  const action = composerKeyAction(event.key, {
    shiftKey: event.shiftKey,
    isComposing: event.nativeEvent.isComposing,
    pickerOpen,
  });
  if (!action) return;
  event.preventDefault();
  handlers[action]();
}

/**
 * Multi-line note input. Enter posts, Shift+Enter adds a line. Typing `@`
 * opens a picker of `mentionableUsers`; picked names are sent as
 * `{user:<uuid>}` tokens.
 */
export function NoteComposer({
  onSubmit,
  isSubmitting = false,
  allowInternalNotes = false,
  mentionableUsers,
  disabled = false,
  className,
  resizable = false,
  placeholder,
  mentionListPlacement = 'overlay',
}: NoteComposerProps) {
  const gt = useGT();
  const [internal, setInternal] = useState(false);
  const listboxId = useId();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [text, setText] = useState('');
  const picked = useRef<TrackedMention[]>([]);
  /** The selection a `beforeinput` is about to replace, in the current draft. */
  const editRange = useRef<{ start: number; end: number } | null>(null);
  const [active, setActive] = useState<ActiveMentionQuery | null>(null);
  const [highlighted, setHighlighted] = useState(0);

  const mentionsEnabled = mentionableUsers !== undefined;
  const suggestions = active
    ? filterMentionCandidates(mentionableUsers ?? NO_CANDIDATES, active.query)
    : [];
  const pickerOpen = suggestions.length > 0;
  const locked = composerLocked(disabled, isSubmitting);
  const canSubmit = !locked && text.trim().length > 0;

  function trackCaret(value: string, caret: number) {
    const next = mentionsEnabled ? findActiveMentionQuery(value, caret) : null;
    setActive(next);
    if (next?.query !== active?.query) setHighlighted(0);
  }

  function pick(candidate: MentionCandidate | undefined) {
    const textarea = textareaRef.current;
    if (!active || !textarea || !candidate) return;
    const caret = textarea.selectionStart;
    const result = insertMention(text, active, caret, candidate);
    picked.current = placeMention(
      picked.current,
      text,
      result.text,
      active.start,
      caret,
      candidate,
    );
    setText(result.text);
    setActive(null);
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(result.caret, result.caret);
    });
  }

  async function submit() {
    if (!canSubmit) return;
    const posted = await onSubmit(
      serializeMentions(text, picked.current),
      allowInternalNotes && internal,
    );
    if (!posted) return;
    setText('');
    picked.current = [];
    setActive(null);
  }

  const keyHandlers: Record<ComposerKeyAction, () => void> = {
    'highlight-next': () =>
      setHighlighted((i) => stepHighlight(i, suggestions.length, 1)),
    'highlight-previous': () =>
      setHighlighted((i) => stepHighlight(i, suggestions.length, -1)),
    pick: () => pick(suggestions[highlighted] ?? suggestions[0]),
    'close-picker': () => setActive(null),
    submit: () => void submit(),
  };

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    runComposerKey(event, pickerOpen, keyHandlers);
  }

  const optionId = (index: number) => `${listboxId}-option-${index}`;

  return (
    <form
      className={cn(
        'relative mt-1.5 flex shrink-0 flex-wrap items-end gap-1.5',
        className,
      )}
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <InternalNoteToggle
        visible={allowInternalNotes}
        checked={internal}
        locked={locked}
        onChange={setInternal}
      />
      <MentionPicker
        open={pickerOpen}
        placement={mentionListPlacement}
        id={listboxId}
        optionId={optionId}
        suggestions={suggestions}
        highlighted={highlighted}
        onHighlight={setHighlighted}
        onPick={pick}
      />
      <ComposerTextarea
        textareaRef={textareaRef}
        text={text}
        placeholder={placeholder ?? composerPlaceholder(gt, mentionsEnabled)}
        locked={locked}
        resizable={resizable}
        mentionsEnabled={mentionsEnabled}
        pickerOpen={pickerOpen}
        listboxId={listboxId}
        activeOptionId={optionId(highlighted)}
        onBeforeInput={(event) => {
          editRange.current = {
            start: event.currentTarget.selectionStart,
            end: event.currentTarget.selectionEnd,
          };
        }}
        onChange={(event) => {
          const next = event.currentTarget.value;
          const range = editRange.current;
          editRange.current = null;
          picked.current = nextDraftMentions(picked.current, text, next, range);
          setText(next);
          trackCaret(next, event.currentTarget.selectionStart);
        }}
        onSelect={(event) =>
          trackCaret(
            event.currentTarget.value,
            event.currentTarget.selectionStart,
          )
        }
        onBlur={() => setActive(null)}
        onKeyDown={handleKeyDown}
      />
      <SubmitNoteButton isSubmitting={isSubmitting} disabled={!canSubmit} />
    </form>
  );
}

function InternalNoteToggle({
  visible,
  checked,
  locked,
  onChange,
}: {
  visible: boolean;
  checked: boolean;
  locked: boolean;
  onChange: (checked: boolean) => void;
}) {
  if (!visible) return null;
  return (
    <label className="flex w-full cursor-pointer select-none items-center gap-1.5 text-[11px] text-muted-foreground">
      <Checkbox
        checked={checked}
        onCheckedChange={(value) => onChange(value === true)}
        disabled={locked}
      />
      <T context="checkbox label: post this note so only the triage team sees it">
        Internal note (hidden from the reporter)
      </T>
    </label>
  );
}

function MentionPicker({
  open,
  placement,
  id,
  optionId,
  suggestions,
  highlighted,
  onHighlight,
  onPick,
}: {
  open: boolean;
  placement: MentionListPlacement;
  id: string;
  optionId: (index: number) => string;
  suggestions: readonly MentionCandidate[];
  highlighted: number;
  onHighlight: (index: number) => void;
  onPick: (candidate: MentionCandidate | undefined) => void;
}) {
  if (!open) return null;
  return (
    <MentionSuggestionList
      placement={placement}
      id={id}
      optionId={optionId}
      suggestions={suggestions}
      highlighted={highlighted}
      onHighlight={onHighlight}
      onPick={onPick}
    />
  );
}

function ComposerTextarea({
  textareaRef,
  text,
  placeholder,
  locked,
  resizable,
  mentionsEnabled,
  pickerOpen,
  listboxId,
  activeOptionId,
  onBeforeInput,
  onChange,
  onSelect,
  onBlur,
  onKeyDown,
}: {
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  text: string;
  placeholder: string;
  locked: boolean;
  resizable: boolean;
  mentionsEnabled: boolean;
  pickerOpen: boolean;
  listboxId: string;
  activeOptionId: string;
  onBeforeInput: (event: FormEvent<HTMLTextAreaElement>) => void;
  onChange: (event: FormEvent<HTMLTextAreaElement>) => void;
  onSelect: (event: FormEvent<HTMLTextAreaElement>) => void;
  onBlur: () => void;
  onKeyDown: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
}) {
  const gt = useGT();
  return (
    <textarea
      ref={textareaRef}
      value={text}
      rows={1}
      onBeforeInput={onBeforeInput}
      onChange={onChange}
      onSelect={onSelect}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      disabled={locked}
      {...mentionComboboxAria({
        mentionsEnabled,
        pickerOpen,
        listboxId,
        activeOptionId,
      })}
      className={composerFieldClass(resizable)}
      placeholder={placeholder}
      aria-label={gt('Add a note', {
        context: 'accessible label for the note input',
      })}
    />
  );
}

function MentionSuggestionList({
  placement,
  id,
  optionId,
  suggestions,
  highlighted,
  onHighlight,
  onPick,
}: {
  placement: MentionListPlacement;
  id: string;
  optionId: (index: number) => string;
  suggestions: readonly MentionCandidate[];
  highlighted: number;
  onHighlight: (index: number) => void;
  onPick: (candidate: MentionCandidate) => void;
}) {
  const gt = useGT();
  return (
    <ul
      id={id}
      role="listbox"
      aria-label={gt('People you can mention', {
        context: 'accessible label for the @mention suggestions list',
      })}
      className={MENTION_LIST_CLASS[placement]}
    >
      {suggestions.map((candidate, index) => (
        <li
          key={candidate.id}
          id={optionId(index)}
          role="option"
          aria-selected={index === highlighted}
          className={cn(
            'cursor-pointer rounded px-2 py-1',
            index === highlighted && 'bg-accent text-accent-foreground',
          )}
          onMouseDown={(event) => {
            // Keep focus (and the caret) in the textarea.
            event.preventDefault();
            onPick(candidate);
          }}
          onMouseEnter={() => onHighlight(index)}
        >
          <div className="font-medium">{candidate.name}</div>
          {candidate.email && (
            <div className="truncate text-[10px] text-muted-foreground">
              {candidate.email}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

function mentionComboboxAria({
  mentionsEnabled,
  pickerOpen,
  listboxId,
  activeOptionId,
}: {
  mentionsEnabled: boolean;
  pickerOpen: boolean;
  listboxId: string;
  activeOptionId: string;
}) {
  if (!mentionsEnabled) return {};
  return {
    role: 'combobox',
    'aria-autocomplete': 'list' as const,
    'aria-expanded': pickerOpen,
    'aria-controls': pickerOpen ? listboxId : undefined,
    'aria-activedescendant': pickerOpen ? activeOptionId : undefined,
  };
}

function SubmitNoteButton({
  isSubmitting,
  disabled,
}: {
  isSubmitting: boolean;
  disabled: boolean;
}) {
  const gt = useGT();
  return (
    <Button
      type="submit"
      size="sm"
      className="h-8 shrink-0 px-2"
      disabled={disabled}
      aria-label={
        isSubmitting
          ? gt('Saving note', {
              context: 'accessible label while posting a note',
            })
          : gt('Add note', {
              context: 'accessible label for the post note button',
            })
      }
    >
      <SendIcon className="size-3.5" />
    </Button>
  );
}
